import React, { useEffect, useMemo, useState, useContext } from 'react'
import { Modal, Button, Form, Input, Select, Switch } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import {
  GET_DEPARTMENTS,
  GET_TEAMS,
  GET_PRIORITY_SLA_CONFIGS,
} from '../graphql/Queries'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

export default function KeywordMappingModal({
  visible,
  onClose,
  onSave,
  initialValues,
  loading,
  isEditMode = false,
  departmentId,
  teamId,
}) {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = initialValues ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null)

  const { data: departmentsData } = useQuery(GET_DEPARTMENTS)
  const { data: teamsData } = useQuery(GET_TEAMS, {
    fetchPolicy: 'network-only',
  })
  const { data: priorityData } = useQuery(GET_PRIORITY_SLA_CONFIGS)

  const priorityOptions = useMemo(() => {
    const all = priorityData?.getPrioritySlaConfigs || []
    const uniqueMap = new Map()
    for (const p of all) {
      if (!uniqueMap.has(p.priorityLevel)) {
        uniqueMap.set(p.priorityLevel, p)
      }
    }
    return Array.from(uniqueMap.values()).map((p) => ({
      label:
        p.priorityLevel.charAt(0).toUpperCase() +
        p.priorityLevel.slice(1).toLowerCase(),
      value: p.id,
    }))
  }, [priorityData])
  useEffect(() => {
    if (visible) {
      const formValues = {
        keyword: '',
        departmentId: undefined,
        teamId: undefined,
        priorityId: undefined,
        isActive: true,
        ...initialValues,
      }

      if (isEditMode && departmentId && teamId) {
        formValues.departmentId = departmentId
        formValues.teamId = teamId
        formValues.priorityId =
          initialValues?.priorityId ?? initialValues?.suggestsPriority?.id
        setSelectedDepartmentId(departmentId)
      }

      form.setFieldsValue(formValues)
      setSelectedDepartmentId(formValues.departmentId || null)
    }
  }, [visible, initialValues, form, isEditMode, departmentId, teamId])

  const handleSave = () => {
    if (!canSubmit) return
    form.validateFields().then((values) => {
      onSave(values)
      form.resetFields()
    })
  }

  const handleCancel = () => {
    onClose()
    form.resetFields()
  }

  const filteredTeams = useMemo(() => {
    if (!selectedDepartmentId || !teamsData?.getTeams) return []
    return teamsData.getTeams.filter(
      (team) => team.departmentId === selectedDepartmentId,
    )
  }, [selectedDepartmentId, teamsData])

  return (
    <Modal
      title={initialValues ? 'Edit Keyword' : 'Add Keyword'}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout='vertical'>
        <Form.Item
          label='Keyword'
          name='keyword'
          rules={[{ required: true, message: 'Please enter a keyword' }]}
        >
          <Input placeholder='Please enter a keyword' />
        </Form.Item>

        {!isEditMode && (
          <>
            <Form.Item
              label='Department'
              name='departmentId'
              rules={[
                { required: true, message: 'Please select a department' },
              ]}
            >
              <Select
                allowClear
                placeholder='Select Department'
                options={
                  departmentsData?.getDepartments?.map((d) => ({
                    label: d.departmentName,
                    value: d.id,
                    priorityOptions,
                  })) || []
                }
                onChange={(value) => {
                  setSelectedDepartmentId(value)
                  form.setFieldsValue({ teamId: undefined })
                }}
              />
            </Form.Item>

            <Form.Item
              label='Team'
              name='teamId'
              rules={[{ required: true, message: 'Please select a team' }]}
            >
              <Select
                allowClear
                placeholder='Select Team'
                options={filteredTeams.map((t) => ({
                  label: t.teamName,
                  value: t.id,
                }))}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          label='Priority'
          name='priorityId'
          rules={[{ required: true, message: 'Please select a priority' }]}
        >
          <Select
            allowClear
            options={priorityOptions}
            placeholder='Select Priority'
            loading={!priorityData}
          />
        </Form.Item>

        <Form.Item label='Active' name='isActive' valuePropName='checked'>
          <Switch defaultChecked disabled={!canSubmit} />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>

          <Button
            type='primary'
            onClick={handleSave}
            loading={loading}
            disabled={loading || !canSubmit}
          >
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
